import { trialFeatures } from 'client/GrowthEngineering/utilities/GrowthConstants';
import PlanApi from 'client/api/v2/PlanApi';
import SubscriptionApi from 'client/api/v2/SubscriptionApi';
import Vue from 'vue';

export const initialState = () => ({
  plan: {
    name: '',
    productCode: '',
    contractType: '',
    pricePerUser: 0,
    committedUsers: 0,
    additionalUsers: 0,
    highWaterMark: 0,
    totalBillingEstimate: 0,
    uncommitedBillingEstimate: 0,
    addOns: [],
    features: [],
  },
  primaryEntitlementFeatures: [],
  planFeatureCodes: [],
  recommendedPlan: '',
  planPriceDataFromSubscription: {
    listPrice: 0,
    annualPrice: 0,
  },
});

const nonUpgradeablePlans = [
  'DEVICES',
  'MSP',
  'PLATFORM-PLUS-2021',
  'PLATFORM-PLUS-EDU',
  'PLATFORM-PLUS-NP',
  'PRIME-PASS',
];

export default {
  namespaced: true,
  state: initialState(),
  actions: {
    async fetchPlan({ commit }) {
      const resp = await PlanApi.fetch();
      if (resp) {
        commit('SET_PLAN', resp.data);
      }
    },

    setRecommendedPlan({ commit }, planName) {
      commit('SET_RECOMMENDED_PLAN', planName);
    },

    async fetchEntitledFeatureCodes({ commit }) {
      try {
        const featureCodeRequest = await PlanApi.fetchEntitledFeatureCodes();

        commit('SET_FEATURE_CODE_LIST', featureCodeRequest.data);
      } catch {
        throw new Error('Unable to fetch plan entitled features.');
      }
    },

    async fetchPrimaryEntitlementFeatures({ commit }, productCode) {
      try {
        const featureCodeRequest = await SubscriptionApi.fetchSubscription(productCode);
        const featureList = featureCodeRequest.data.features || [];

        commit('SET_PRIMARY_FEATURE_LIST', featureList);
      } catch {
        commit('SET_PRIMARY_FEATURE_LIST', []);
        throw new Error('Unable to fetch subscription information.');
      }
    },

    async fetchPlanPriceFromSubscriptions({ commit }, productCode) {
      try {
        const planRequest = await SubscriptionApi.fetchSubscription(productCode);
        const { annualPrice, listPrice } = planRequest.data;

        commit('SET_PRIMARY_PRICE', { annualPrice, listPrice });
      } catch {
        commit('SET_PRIMARY_PRICE', {});
        throw new Error('Unable to fetch subscription information.');
      }
    },
  },

  getters: {
    canUpgradePlan(state) {
      if (nonUpgradeablePlans.includes(state.plan.productCode)) {
        return false;
      }

      const featureCodes = state.planFeatureCodes.map(feature => feature.code);
      const isFullyUpgraded = featureCodes.includes(trialFeatures.osPatchManagement.featureCode)
        && featureCodes.includes(trialFeatures.zeroTrust.featureCode);

      return !isFullyUpgraded;
    },

    getRecommendedPlan(state) {
      return state.recommendedPlan;
    },

    getPrimaryEntitlmentFeatures(state) {
      return state.primaryEntitlementFeatures;
    },

    hasAnnualContractType(state) {
      return state.plan.contractType.toLowerCase() === 'annual';
    },

    hasPlan(state) {
      // Note: Currently checking both contract and product code to make sure we're covering
      // edge case in latency of Mulesoft/ BSS update.
      // Now that 2021-08-08-new-self-serve-banner-text
      // is productized, using isCustomer would be an alternative to hasPlan
      return (!!state.plan.productCode || !!state.plan.contractType);
    },

    planHasFeature(state) {
      return (featureCode) => state.planFeatureCodes.some(({ code }) => code === featureCode);
    },
  },

  mutations: {
    SET_PLAN(state, plan) {
      Object.keys(state.plan).forEach((field) => {
        Vue.set(state.plan, field, plan[field]);
      });
    },

    SET_RECOMMENDED_PLAN(state, planName) {
      Vue.set(state, 'recommendedPlan', planName);
    },

    SET_FEATURE_CODE_LIST(state, codes) {
      Vue.set(state, 'planFeatureCodes', codes);
    },

    SET_PRIMARY_FEATURE_LIST(state, list) {
      Vue.set(state, 'primaryEntitlementFeatures', list);
    },

    SET_PRIMARY_PRICE(state, list) {
      Vue.set(state, 'planPriceDataFromSubscription', list);
    },
  },
};
